import React, { useMemo, useRef } from 'react';
import clsx from 'clsx';
import composeRefs from '@seznam/compose-react-refs';
import { IInputMaskOptions } from '@hse-design/core/lib/es/inputMask';

import { useDelegateFocus, useFocus } from '../../common/hooks';
import { IntrinsicPropsWithoutRef } from '../../common/types';
import { useInputMask } from './useInputMask';
import { Icon, IconSize } from '../Icon';

export enum InputSize {
  small = 'small',
  medium = 'medium',
  large = 'large'
}

export interface InputProps
  extends Omit<IntrinsicPropsWithoutRef<'input'>, 'size' | 'onClick'> {
  /**
   * Размер (`small`, `medium`, `large`)
   */
  size?: InputSize;
  /**
   * Состояние ошибки
   */
  error?: boolean;
  /**
   * Растягивает компонент на всю ширину родительского элемента
   */
  fullWidth?: boolean;
  /**
   * Компонент иконки слева
   */
  leftIcon?: React.ElementType;
  /**
   * Компонент иконки справа
   */
  rightIcon?: React.ElementType;
  /**
   * Слот слева
   */
  left?: React.ReactNode;
  /**
   * Слот справа
   */
  right?: React.ReactNode;
  /**
   * Ref на input-элемент
   */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Обработчик нажатия на Input
   */
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  /**
   * Проп для задания маски ввода
   */
  mask?: IInputMaskOptions['maskOptions'];
  /**
   * Вызывается при использовании input с маской.
   *
   * @param unmaskedValue - значение без применения маски
   */
  onUnmaskedChange?: (unmaskedValue: string | undefined) => void;
  /**
   * Дополнительные css-классы элементов:
   * * root - внешний контейнер
   * * input - элемент input
   * * icon - иконки слева и справа Input
   * * iconLeft - иконка слева Input
   * * iconRight - иконка справа Input
   */
  classes?: {
    root?: string;
    icon?: string;
    iconLeft?: string;
    iconRight?: string;
    input?: string;
  };
}

/**
 * @deprecated используйте IInputProps
 */
export type IInputProps = InputProps;

export type InputType = React.ForwardRefExoticComponent<
  InputProps & React.RefAttributes<HTMLDivElement>
> & {
  Size: typeof InputSize;
};

const InputForwardedRef: any = React.forwardRef<HTMLDivElement, InputProps>(
  (
    {
      size = InputSize.medium,
      error,
      fullWidth,
      className,
      left,
      right,
      leftIcon,
      rightIcon,
      classes,
      onClick,
      onUnmaskedChange,
      onInput,
      mask,
      inputRef: inputRefProp,
      style,
      ...inputProps
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const delegateProps = useDelegateFocus<HTMLDivElement, HTMLInputElement>(
      inputRef,
      { onClick }
    );
    const { focused, ...focusProps } = useFocus(inputProps);

    const maskInputProps = useInputMask({
      mask,
      onInput,
      onUnmaskedChange,
      inputRef
    });

    const iconSize = useMemo(
      () => (size === InputSize.small ? IconSize.small : IconSize.medium),
      [size]
    );

    return (
      <div
        ref={ref}
        className={clsx(
          'hse-Input',
          className,
          `hse-Input_size_${size}`,
          classes?.root,
          {
            'hse-Input_focus': focused,
            'hse-Input_error': error,
            'hse-Input_disabled': inputProps.disabled,
            'hse-Input_fullWidth': fullWidth
          }
        )}
        style={style}
        {...delegateProps}
      >
        {(left || leftIcon) && (
          <div
            className={clsx(
              'hse-Input__icon',
              'hse-Input__icon_left',
              classes?.icon,
              classes?.iconLeft
            )}
          >
            {left ? left : <Icon icon={leftIcon} size={iconSize} />}
          </div>
        )}
        <input
          ref={composeRefs(inputRef, inputRefProp)}
          {...inputProps}
          {...focusProps}
          {...maskInputProps}
          className={clsx('hse-Input__input', classes?.input)}
        />
        {(right || rightIcon) && (
          <div
            className={clsx(
              'hse-Input__icon',
              'hse-Input__icon_right',
              classes?.icon,
              classes?.iconRight
            )}
          >
            {right ? right : <Icon icon={rightIcon} size={iconSize} />}
          </div>
        )}
      </div>
    );
  }
);

InputForwardedRef.Size = InputSize;

export const Input: InputType = InputForwardedRef;
